import type { OutboxOp, PersistedOp } from "@/lib/mutators/types";

export interface EnqueuePlan {
  remove: number[];
  add: OutboxOp | null;
}

// Ops on one task, named by the task's own id.
const TARGETS_ONE_TASK = new Set<OutboxOp["name"]>([
  "createTask", "updateTask", "completeTask", "uncompleteTask", "deleteTask", "reorderTask", "reorderMyDay",
]);

const targetOf = (op: OutboxOp): string | null => (TARGETS_ONE_TASK.has(op.name) ? (op.args as { id: string }).id : null);

// A task made and deleted before anything about it left the device never has to reach the
// server, nor do the edits and subtasks made on it in between.
function cancelledByDelete(unsent: PersistedOp[], id: string): number[] | null {
  if (!unsent.some((op) => op.name === "createTask" && op.args.id === id)) return null;
  const gone = new Set([id]);
  for (const op of unsent) {
    if (op.name === "createTask" && op.args.parentId && gone.has(op.args.parentId)) gone.add(op.args.id);
  }
  return unsent.filter((op) => gone.has(targetOf(op) ?? "")).map((op) => op.seq);
}

const sameKeys = (a: object, b: object) => {
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((key) => key in b);
};

// How the outbox changes when `op` is added: consecutive edits of the same fields of a task
// merge, so typing notes does not queue an op per keystroke, and the merged op still carries
// every field a resend needs. Ops up to `sentThrough` may already be on the server and are
// left alone.
export function planEnqueue(outbox: readonly PersistedOp[], op: OutboxOp, sentThrough: number): EnqueuePlan {
  const unsent = outbox.filter((row) => row.seq > sentThrough).sort((a, b) => a.seq - b.seq);

  if (op.name === "deleteTask") {
    const cancelled = cancelledByDelete(unsent, op.args.id);
    if (cancelled) return { remove: cancelled, add: null };
  }

  const last = unsent[unsent.length - 1];
  if (
    op.name === "updateTask" && last?.name === "updateTask" && last.args.id === op.args.id &&
    sameKeys(last.args.fields, op.args.fields)
  ) {
    const mergedIds = [...(last.mergedIds ?? []), last.id];
    return { remove: [last.seq], add: { ...op, mergedIds } };
  }

  return { remove: [], add: op };
}
